// Lesson.js
import React, {useEffect, useState} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Typography, Button } from '@mui/material';
import { useTranslation } from "react-i18next";
import { collection, doc, getDoc, getDocs, query, where  } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getAuth } from 'firebase/auth';
import StudentNavbar from '../components/StudentNavbar';
import TeacherNavbar from '../components/TeacherNavbar';
import { pageGradientBackground } from '../theme/tokens';
import SwfPlayer from './SwfPlayer';

function Lesson({ chrome = "auto", roleOverride = null }) {
  const { t } = useTranslation();
  const [userRole, setUserRole] = useState(roleOverride);
  const [isCoachForClass, setIsCoachForClass] = useState(false);
  const auth = getAuth();

  const { groupId } = useParams();
  const location = useLocation();
  
  // Destructure the file data passed through state
  const { fileUrl, fileType } = location.state || {};
  const effectiveUserRole = roleOverride || userRole;
  const showStandaloneChrome = chrome !== "none";

  useEffect(() => {
    if (roleOverride) {
      setUserRole(roleOverride);
      return;
    }

    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (!user) return;
  
      try {
        // Check in "students" collection
        const studentQuery = query(collection(db, 'students'), where('uid', '==', user.uid));
        const studentSnapshot = await getDocs(studentQuery);
  
        if (!studentSnapshot.empty) {
          setUserRole('student');
          return;
        }
  
        // Check in "teachers" collection
        const teacherQuery = query(collection(db, 'teachers'), where('uid', '==', user.uid));
        const teacherSnapshot = await getDocs(teacherQuery);
  
        if (!teacherSnapshot.empty) {
          setUserRole('teacher');
          return;
        }
  
        // Default to null if not found
        setUserRole(null);
  
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      }
    };
  
    fetchUserRole();
  }, [auth, roleOverride]);

  useEffect(() => {
    const resolveTutorTypeForClass = async () => {
      if (!groupId) {
        setIsCoachForClass(false);
        return;
      }

      try {
        const groupSnap = await getDoc(doc(db, 'groups', groupId));
        if (!groupSnap.exists()) {
          setIsCoachForClass(false);
          return;
        }

        const teacherId = groupSnap.data()?.teacherId;
        if (!teacherId) {
          setIsCoachForClass(false);
          return;
        }

        const teacherSnap = await getDoc(doc(db, 'teachers', teacherId));
        if (!teacherSnap.exists()) {
          setIsCoachForClass(false);
          return;
        }

        const tutorType = String(teacherSnap.data()?.tutorType || 'Teacher').trim().toLowerCase();
        setIsCoachForClass(tutorType === 'coach');
      } catch (error) {
        console.error('Error resolving tutor type for class:', error);
        setIsCoachForClass(false);
      }
    };

    resolveTutorTypeForClass();
  }, [groupId]);
  


  // If no file was passed, display an error or fallback
  if (!fileUrl || !fileType) {
    return (
      <div style={{ textAlign: 'center', marginTop: '40px' }}>
        <Typography variant="h6">
         {t("Sorry, no file data was provided.")}
        </Typography>
      </div>
    );
  }


  return (
    <div
      style={{
        background: showStandaloneChrome && effectiveUserRole === 'student' ? pageGradientBackground : undefined,
        minHeight: showStandaloneChrome && effectiveUserRole === 'student' ? '100vh' : undefined,
      }}
    >

      {showStandaloneChrome && effectiveUserRole === 'student' && <StudentNavbar />}
      {showStandaloneChrome && effectiveUserRole === 'teacher' && <TeacherNavbar />}


      <div style={{ minHeight: '100vh', padding: '20px' }}>
        {fileType === 'image' ? (
          /* IMAGE SECTION */
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <img
              src={fileUrl}
              alt={t('Image')}
              style={{ maxWidth: '95%', maxHeight: '95vh', objectFit: 'contain' }}
            />
          </div>
        ) : fileType === 'file' || fileType === 'exercises' ? (
          /* PDF SECTION */
          <iframe
            src={fileUrl}
            title={t("PDF Viewer")}
            style={{
              width: '100%',
              height: '100vh',
              border: 'none',
              display: 'block',
              margin: '0 auto',
            }}
          />
        ) : fileType === 'video' ? (
          /* VIDEO SECTION */
          <>
            {/* "Right section" on top (full width) */}
            <div
              style={{
                width: '100%',
                backgroundColor: '#fff',
                padding: '10px',
              }}
            >
              <h2 style={{display: 'flex', justifyContent: 'center',}}>
                {isCoachForClass ? t("Welcome to this video program:") : t("Welcome to this video lesson:")}
              </h2>

              <div style={{ lineHeight: '1.6', marginTop: '10px', display: 'flex', justifyContent: 'center', }}>
                {isCoachForClass
                  ? t("Through this video program, we aim to enrich your knowledge and develop your skills. We recommend focusing closely and making the most of the information and examples provided.")
                  : t("Through this video lesson, we aim to enrich your knowledge and develop your skills. We recommend focusing closely and making the most of the information and examples provided.")}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '10px' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const newPath = window.location.pathname.replace('/lesson', '');
                    window.location.href = newPath;
                  }}
                >
                 {t("Back to class page")}
                </Button>
              </div>
            </div>

            {/* Video centered at 80% width */}
            <div style={{ display: 'flex', justifyContent: 'center'}}>
              <video
                src={fileUrl}
                style={{ width: '90%', height: '80vh' }}
                controls
                autoPlay
              />
            </div>
          </>
        ) : fileType === 'audio' ? (
          /* AUDIO SECTION */
          <>
            <div
              style={{
                width: '100%',
                backgroundColor: '#fff',
                padding: '10px',
              }}
            >
              <h2 style={{ display: 'flex', justifyContent: 'center' }}>
                {isCoachForClass ? t("Welcome to this audio program:") : t("Welcome to this audio lesson:")}
              </h2>

              <div style={{ lineHeight: '1.6', marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                {isCoachForClass
                  ? t("Through this audio program, we aim to enrich your knowledge and develop your skills. We recommend listening closely and making the most of the information provided.")
                  : t("Through this audio lesson, we aim to enrich your knowledge and develop your skills. We recommend listening closely and making the most of the information provided.")}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '10px' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const newPath = window.location.pathname.replace('/lesson', '');
                    window.location.href = newPath;
                  }}
                >
                  {t("Back to class page")}
                </Button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
              <audio src={fileUrl} style={{ width: '90%' }} controls autoPlay>
                {t("Your browser does not support the audio element.")}
              </audio>
            </div>
          </>
        ) : fileType === 'swf' ? (
          /* FLASH ANIMATION SECTION */
          <>
            <div
              style={{
                width: '100%',
                backgroundColor: '#fff',
                padding: '10px',
              }}
            >
              <h2 style={{ display: 'flex', justifyContent: 'center' }}>
                {t("Welcome to this animation:")}
              </h2>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '10px' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const newPath = window.location.pathname.replace('/lesson', '');
                    window.location.href = newPath;
                  }}
                >
                  {t("Back to class page")}
                </Button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '90%' }}>
                <SwfPlayer url={fileUrl} />
              </div>
            </div>
          </>
        ) : (
          /* Fallback if fileType is unknown */
          <Typography variant="h6" align="center" sx={{ marginTop: '40px' }}>
           {t("This file type is not supported.")}
          </Typography>
        )}
      </div>
    </div>
  );
}

export default Lesson;
